import React from 'react';

import { Clock, User, X } from 'lucide-react';

import { useGetLineItemAuditLogsQuery, useGetLineItemQuery } from '../api';

import type { LineItemAuditLog } from '../types';

interface LineItemAuditLogModalProps {
	isOpen: boolean;
	onClose: () => void;
	lineItemId: string;
	lineItemDescription: string;
}

/**
 * Formats a date string into a readable format
 */
const formatDate = (dateString: string): string => {
	const date = new Date(dateString);
	return date.toLocaleString('en-US', {
		year: 'numeric',
		month: 'short',
		day: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
	});
};

/**
 * Formats a field name into a readable label
 */
const formatFieldName = (field: string): string => {
	const fieldLabels: Record<string, string> = {
		description: 'Description',
		amount: 'Amount',
		fee_type_id: 'Fee Type',
		processor_id: 'Processor',
		network_id: 'Network',
	};
	return (
		fieldLabels[field] ||
		field.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase())
	);
};

/**
 * Formats a value for display
 */
const formatValue = (value: any, field?: string): string => {
	if (value === null || value === undefined) {
		return '—';
	}
	if (typeof value === 'number') {
		// Format rate as percentage to match table display
		if (field === 'rate') {
			return `${(value * 100).toFixed(3)}%`;
		}
		// Use 3 decimal places for per_transaction_fee to match table display
		if (field === 'per_transaction_fee') {
			return value.toFixed(3);
		}
		return value.toFixed(2);
	}
	return String(value);
};

/**
 * Single audit log entry component
 */
const AuditLogEntry: React.FC<{ log: LineItemAuditLog }> = ({ log }) => {
	const changes = log.changes || {};
	const changeFields = Object.keys(changes);

	return (
		<div className="border-l-2 border-indigo-300 pl-4 pb-4 relative">
			{/* Timeline dot */}
			<div className="absolute top-0 -left-1.5 w-2.5 h-2.5 bg-indigo-500 rounded-full" />

			{/* Header */}
			<div className="flex items-center gap-3 mb-2 text-sm text-gray-500">
				<div className="flex items-center gap-1">
					<Clock className="w-4 h-4" />
					<span>{formatDate(log.created_at)}</span>
				</div>
				<div className="flex items-center gap-1">
					<User className="w-4 h-4" />
					<span>{log.user_name || log.user_email || 'User'}</span>
				</div>
				{log.processor_name && (
					<span className="px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700">
						{log.processor_name}
					</span>
				)}
				<span
					className={`px-2 py-0.5 rounded-full text-xs font-medium ${
						log.action === 'create'
							? 'bg-green-100 text-green-700'
							: log.action === 'delete'
								? 'bg-red-100 text-red-700'
								: 'bg-blue-100 text-blue-700'
					}`}
				>
					{log.action}
				</span>
			</div>

			{/* Changes */}
			{changeFields.length > 0 && (
				<div className="space-y-2">
					{changeFields.map((field) => {
						const change = changes[field];
						return (
							<div key={field} className="bg-gray-50 rounded-lg p-3">
								<div className="text-xs font-medium text-gray-500 mb-1">
									{formatFieldName(field)}
								</div>
								<div className="flex items-center gap-2 text-sm">
									<span className="text-red-600 line-through">
										{formatValue(change.old, field)}
									</span>
									<span className="text-gray-400">→</span>
									<span className="text-green-600 font-medium">
										{formatValue(change.new, field)}
									</span>
								</div>
							</div>
						);
					})}
				</div>
			)}
		</div>
	);
};

/**
 * LineItemAuditLogModal - Modal to display audit history for a line item
 */
const LineItemAuditLogModal: React.FC<LineItemAuditLogModalProps> = ({
	isOpen,
	onClose,
	lineItemId,
	lineItemDescription,
}) => {
	const { data, isLoading, error, refetch } = useGetLineItemAuditLogsQuery(
		lineItemId,
		{
			skip: !isOpen || !lineItemId,
			refetchOnMountOrArgChange: true, // Always fetch fresh data when modal opens
		},
	);

	// Refetch when modal opens to ensure fresh data
	React.useEffect(() => {
		if (isOpen && lineItemId) {
			refetch();
		}
	}, [isOpen, lineItemId, refetch]);

	// Fetch line item details to get processor information
	const { data: lineItemData } = useGetLineItemQuery(lineItemId, {
		skip: !isOpen || !lineItemId,
	});

	if (!isOpen) return null;

	const auditLogs = data?.audit_logs || [];
	const processorName = lineItemData?.line_item?.processor?.name || '';

	return (
		<div className="fixed inset-0 z-50 overflow-y-auto">
			{/* Backdrop */}
			<div
				className="fixed inset-0 bg-black/50 transition-opacity"
				onClick={onClose}
			/>

			{/* Modal */}
			<div className="flex min-h-full items-center justify-center p-4">
				<div
					className="relative bg-white rounded-xl shadow-xl max-w-xl w-full max-h-[80vh] overflow-hidden"
					onClick={(e) => e.stopPropagation()}
				>
					{/* Header */}
					<div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
						<div>
							<h2 className="text-lg font-semibold text-gray-900">
								Edit History
							</h2>
							<p className="text-sm text-gray-500 mt-0.5 truncate max-w-[350px]">
								{lineItemDescription}
							</p>
							{processorName && (
								<p className="text-xs text-indigo-600 font-medium mt-1">
									{processorName}
								</p>
							)}
						</div>
						<button
							onClick={onClose}
							className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
							aria-label="Close modal"
						>
							<X className="w-5 h-5" />
						</button>
					</div>

					{/* Content */}
					<div className="px-6 py-4 overflow-y-auto max-h-[60vh]">
						{isLoading ? (
							<div className="flex items-center justify-center py-8">
								<div className="w-6 h-6 border-2 border-indigo-500 border-t-transparent rounded-full animate-spin" />
								<span className="ml-2 text-gray-500">Loading history...</span>
							</div>
						) : error ? (
							<div className="text-center py-8">
								<p className="text-red-500">Failed to load audit history</p>
								<button
									onClick={() => window.location.reload()}
									className="mt-2 text-sm text-indigo-600 hover:text-indigo-700"
								>
									Try again
								</button>
							</div>
						) : auditLogs.length === 0 ? (
							<div className="text-center py-8">
								<Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
								<p className="text-gray-500">No edit history available</p>
								<p className="text-sm text-gray-400 mt-1">
									Changes to this item will appear here
								</p>
							</div>
						) : (
							<div className="space-y-4">
								{auditLogs.map((log) => (
									<AuditLogEntry key={log.id} log={log} />
								))}
							</div>
						)}
					</div>

					{/* Footer */}
					<div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
						<p className="text-xs text-gray-400 text-center">
							{auditLogs.length > 0
								? `${auditLogs.length} change${auditLogs.length === 1 ? '' : 's'} recorded`
								: 'No changes recorded'}
						</p>
					</div>
				</div>
			</div>
		</div>
	);
};

export default LineItemAuditLogModal;
